import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Space, message, Row, Col, Input, DatePicker, Upload, Modal, Alert, Tooltip, Statistic } from 'antd';
import { SyncOutlined, DownloadOutlined, UploadOutlined, CloudDownloadOutlined, EyeOutlined, FileTextOutlined, DollarOutlined } from '@ant-design/icons';
import api from '../services/api';
import EntradaEstoqueNFe from '../components/EntradaEstoqueNFe';

const fmtMoeda = v => (v == null ? '-' : 'R$ ' + parseFloat(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 }));
const fmtData = v => (v ? new Date(v).toLocaleDateString('pt-BR') : '-');
const fmtHora = v => (v ? new Date(v).toLocaleString('pt-BR') : '-');
const fmtCnpj = v => (v && v.length === 14 ? v.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5') : v || '-');

export default function NFERecebidas() {
  const [nfes, setNfes] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [busca, setBusca] = useState('');
  const [periodo, setPeriodo] = useState([null, null]);
  const [detalhe, setDetalhe] = useState(null);

  useEffect(() => { carregar(); }, []);

  const carregar = () => {
    setLoading(true);
    api.get('/nfe/recebidas').then(r => setNfes(r.data || [])).catch(() => {}).finally(() => setLoading(false));
    api.get('/nfe/recebidas/status').then(r => setStatus(r.data)).catch(() => {});
  };

  const sincronizar = async () => {
    setSincronizando(true);
    try {
      const r = await api.post('/nfe/recebidas/sincronizar');
      if (r.data.bloqueado) message.warning(r.data.mensagem, 8);
      else message.success(r.data.mensagem, 8);
      carregar();
    } catch (e) { message.error('Erro: ' + (e.response?.data?.erro || e.message), 8); }
    finally { setSincronizando(false); }
  };

  const enviarXml = async ({ file, onSuccess, onError }) => {
    setEnviando(true);
    try {
      const fd = new FormData();
      fd.append('arquivos', file);
      const r = await api.post('/nfe/recebidas/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const imp = r.data.importadas?.[0];
      const err = r.data.erros?.[0];
      if (imp) {
        message.success(`${file.name}: NF-e ${imp.numero || ''} de ${imp.fornecedor || '-'} (${fmtMoeda(imp.valor)}) ${imp.inserida ? 'importada' : 'atualizada'}`, 6);
        if (imp.aviso) message.warning(imp.aviso, 8);
        onSuccess && onSuccess(r.data);
      } else {
        message.error(`${file.name}: ${err?.erro || 'erro ao importar'}`, 8);
        onError && onError(new Error(err?.erro));
      }
      carregar();
    } catch (e) { message.error(file.name + ': ' + (e.response?.data?.erro || e.message), 8); onError && onError(e); }
    finally { setEnviando(false); }
  };

  const darCiencia = async (nfe) => {
    try {
      const r = await api.post('/nfe/recebidas/' + nfe.id + '/ciencia');
      if (r.data.ok) message.success(r.data.completa ? 'Ciência registrada e XML completo baixado.' : 'Ciência registrada (cStat ' + r.data.cStat + '). O XML completo virá na próxima sincronização.', 8);
      else message.error('SEFAZ não aceitou a manifestação (cStat ' + r.data.cStat + ')', 8);
      carregar();
    } catch (e) { message.error('Erro: ' + (e.response?.data?.erro || e.message), 8); }
  };

  const baixarXML = async (nfe) => {
    try {
      const res = await api.get('/nfe/recebidas/' + nfe.id + '/xml', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'NFe-' + (nfe.chave_acesso || nfe.nsu) + '.xml'; a.click();
    } catch { message.error('Erro ao baixar XML'); }
  };

  const verDetalhe = async (nfe) => {
    try { const r = await api.get('/nfe/recebidas/' + nfe.id); setDetalhe(r.data); }
    catch { message.error('Erro ao carregar detalhes'); }
  };

  const b = busca.toLowerCase();
  const nfesFiltradas = nfes.filter(n => {
    if (b && !((n.razao_social_emit || '').toLowerCase().includes(b) || (n.cnpj_emit || '').includes(b) || (n.chave_acesso || '').includes(b) || (n.numero || '').includes(b))) return false;
    if (periodo?.[0] && n.data_emissao && new Date(n.data_emissao) < periodo[0].startOf('day').toDate()) return false;
    if (periodo?.[1] && n.data_emissao && new Date(n.data_emissao) > periodo[1].endOf('day').toDate()) return false;
    return true;
  });
  const totalValor = nfesFiltradas.reduce((s, n) => s + (n.situacao === 'cancelada' ? 0 : parseFloat(n.valor_total || 0)), 0);
  const bloqueado = status?.bloqueado_ate && new Date(status.bloqueado_ate) > new Date();

  const corSituacao = { autorizada: 'green', cancelada: 'red', denegada: 'orange' };

  return (
    <>
      <Row gutter={16} style={{ marginBottom: 12 }}>
        <Col span={6}><Card size="small"><Statistic title="Notas" value={nfesFiltradas.length} prefix={<FileTextOutlined />} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="Valor total (sem canceladas)" value={fmtMoeda(totalValor)} prefix={<DollarOutlined style={{ color: '#52c41a' }} />} /></Card></Col>
        <Col span={12}>
          <Card size="small">
            <div style={{ fontSize: 12, color: '#888' }}>Última consulta à SEFAZ: <strong>{fmtHora(status?.ultima_consulta)}</strong> — retorno: {status?.ultimo_cstat || '-'} {status?.ultimo_motivo || ''}</div>
            <div style={{ fontSize: 12, color: bloqueado ? '#fa8c16' : '#888' }}>{bloqueado ? 'Nova consulta liberada a partir de ' + fmtHora(status.bloqueado_ate) : 'Consulta liberada.'} Último NSU: {status?.ult_nsu || '-'}</div>
          </Card>
        </Col>
      </Row>

      <Card size="small" style={{ marginBottom: 8 }}>
        <Row gutter={8}>
          <Col span={8}><Input.Search placeholder="Buscar por fornecedor, CNPJ, número ou chave..." value={busca} onChange={e => setBusca(e.target.value)} allowClear /></Col>
          <Col span={7}><DatePicker.RangePicker format="DD/MM/YYYY" onChange={v => setPeriodo(v || [null, null])} style={{ width: '100%' }} /></Col>
          <Col span={4}>
            <Tooltip title={bloqueado ? 'A SEFAZ permite uma consulta por hora' : 'Buscar notas novas na SEFAZ'}>
              <Button type="primary" icon={<SyncOutlined />} loading={sincronizando} onClick={sincronizar} block>Sincronizar SEFAZ</Button>
            </Tooltip>
          </Col>
          <Col span={5}>
            <Upload accept=".xml,text/xml,application/xml" multiple showUploadList={false} customRequest={enviarXml}>
              <Button icon={<UploadOutlined />} loading={enviando} block>Importar XML</Button>
            </Upload>
          </Col>
        </Row>
      </Card>

      <Card size="small">
        <Table dataSource={nfesFiltradas} rowKey="id" size="small" loading={loading} pagination={{ pageSize: 20 }}
          locale={{ emptyText: 'Nenhuma NF-e recebida. Clique em Sincronizar SEFAZ ou importe o XML.' }}
          columns={[
            { title: 'Nº', dataIndex: 'numero', width: 90, render: (v, r) => v ? <Tag color="blue">{v}{r.serie ? '/' + r.serie : ''}</Tag> : <Tag>resumo</Tag> },
            { title: 'Emissão', dataIndex: 'data_emissao', width: 100, render: fmtData },
            { title: 'Fornecedor', dataIndex: 'razao_social_emit', ellipsis: true, render: (v, r) => <><div><strong>{v || '-'}</strong></div><div style={{ fontSize: 11, color: '#888' }}>{fmtCnpj(r.cnpj_emit)}</div></> },
            { title: 'Valor', dataIndex: 'valor_total', width: 120, align: 'right', render: fmtMoeda },
            { title: 'Situação', dataIndex: 'situacao', width: 100, render: v => <Tag color={corSituacao[v] || 'default'}>{v || '-'}</Tag> },
            { title: 'XML', dataIndex: 'tipo', width: 95, render: (v, r) => v === 'completa'
                ? <Tag color="green">completo</Tag>
                : <Tag color="gold">{r.manifestacao ? 'aguardando' : 'resumo'}</Tag> },
            { title: 'Origem', dataIndex: 'origem', width: 80, render: v => <Tag>{v === 'manual' ? 'upload' : 'SEFAZ'}</Tag> },
            { title: 'Chave', dataIndex: 'chave_acesso', width: 130, ellipsis: true, render: v => <span style={{ fontSize: 10 }}>{v || '-'}</span> },
            { title: 'Ações', width: 120, render: (_, r) => (
              <Space size={4}>
                <Tooltip title="Ver itens"><Button size="small" icon={<EyeOutlined />} onClick={() => verDetalhe(r)} disabled={r.tipo !== 'completa'} /></Tooltip>
                <EntradaEstoqueNFe nota={r} />
                <Tooltip title="Baixar XML"><Button size="small" icon={<DownloadOutlined />} onClick={() => baixarXML(r)} /></Tooltip>
                {r.tipo !== 'completa' && r.origem !== 'manual' && (
                  <Tooltip title="Dar ciência da operação e baixar o XML completo"><Button size="small" type="primary" ghost icon={<CloudDownloadOutlined />} onClick={() => darCiencia(r)} /></Tooltip>
                )}
              </Space>
            ) },
          ]}
        />
      </Card>

      <Modal open={!!detalhe} onCancel={() => setDetalhe(null)} footer={null} width={900} title={detalhe ? `NF-e ${detalhe.numero || ''} — ${detalhe.razao_social_emit || ''}` : ''}>
        {detalhe && (
          <>
            <Row gutter={16} style={{ marginBottom: 12, fontSize: 12 }}>
              <Col span={8}>CNPJ: <strong>{fmtCnpj(detalhe.cnpj_emit)}</strong></Col>
              <Col span={8}>Emissão: <strong>{fmtData(detalhe.data_emissao)}</strong></Col>
              <Col span={8}>Total: <strong>{fmtMoeda(detalhe.valor_total)}</strong></Col>
              <Col span={24} style={{ marginTop: 4 }}>Chave: <span style={{ fontSize: 11 }}>{detalhe.chave_acesso}</span> — Protocolo: {detalhe.protocolo || '-'}</Col>
            </Row>
            {detalhe.situacao === 'cancelada' && <Alert type="error" showIcon message="Esta nota foi cancelada pelo emitente." style={{ marginBottom: 12 }} />}
            <Table dataSource={detalhe.itens || []} rowKey={(r, i) => r.nItem || i} size="small" pagination={false}
              columns={[
                { title: '#', dataIndex: 'nItem', width: 40 },
                { title: 'Código', dataIndex: 'cProd', width: 100 },
                { title: 'Descrição', dataIndex: 'xProd', ellipsis: true },
                { title: 'NCM', dataIndex: 'NCM', width: 90 },
                { title: 'CFOP', dataIndex: 'CFOP', width: 60 },
                { title: 'Qtd', dataIndex: 'qCom', width: 80, align: 'right', render: v => v?.toLocaleString('pt-BR') },
                { title: 'Un', dataIndex: 'uCom', width: 50 },
                { title: 'Unit.', dataIndex: 'vUnCom', width: 100, align: 'right', render: fmtMoeda },
                { title: 'Total', dataIndex: 'vProd', width: 110, align: 'right', render: fmtMoeda },
              ]} />
          </>
        )}
      </Modal>
    </>
  );
}
